/**
 * JobAssigneesContext + hook.
 *
 * Owns the "Job Assigned To" name list — fetched once from
 * ``/api/job-assignees`` and re-fetched after any add/edit/remove
 * mutation. Every SelectField for ``job_assigned_to`` reads the list
 * from here so a single edit propagates across the whole app without
 * a reload.
 *
 * Falls back to the historical hard-coded eight names when the API
 * hasn't returned yet, so the picker never renders empty on first
 * paint.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { authFetch, authJsonFetch } from '../lib/api';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const FALLBACK = ['Bri', 'Britt', 'Cece', 'Kim', 'Leah', 'Nicole', 'Parveen', 'William'];

const JobAssigneesContext = createContext({
  assignees: [],
  names: FALLBACK,
  loading: false,
  reload: async () => {},
  createAssignee: async () => null,
  renameAssignee: async () => null,
  deleteAssignee: async () => null,
});

export const JobAssigneesProvider = ({ children }) => {
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(false);
  // Once the first successful load lands, we swap `names` off the
  // fallback and onto whatever the API returned — including an empty
  // list, which is a legitimate state (all users removed).
  const loadedRef = useRef(false);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authFetch(`${API}/job-assignees`);
      if (!res.ok) return;
      const j = await res.json();
      const rows = Array.isArray(j?.data) ? j.data : [];
      // Sort by name asc — backend already does this but a client-side
      // safety net keeps deterministic order.
      rows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setAssignees(rows);
      loadedRef.current = true;
    } catch (e) {
      // Silent — SelectField still has FALLBACK to work with.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const createAssignee = useCallback(async (name) => {
    const res = await authJsonFetch(`${API}/job-assignees`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to add');
    }
    await reload();
    return await res.json();
  }, [reload]);

  const renameAssignee = useCallback(async (id, name) => {
    const res = await authJsonFetch(`${API}/job-assignees/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to rename');
    }
    await reload();
    return await res.json();
  }, [reload]);

  const deleteAssignee = useCallback(async (id) => {
    const res = await authJsonFetch(`${API}/job-assignees/${id}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to delete');
    }
    await reload();
    return true;
  }, [reload]);

  const value = useMemo(() => ({
    assignees,
    names: loadedRef.current
      ? assignees.map(a => a.name).filter(Boolean)
      : FALLBACK,
    loading,
    reload,
    createAssignee,
    renameAssignee,
    deleteAssignee,
  }), [assignees, loading, reload, createAssignee, renameAssignee, deleteAssignee]);

  return (
    <JobAssigneesContext.Provider value={value}>
      {children}
    </JobAssigneesContext.Provider>
  );
};

export const useJobAssignees = () => useContext(JobAssigneesContext);
